import React from 'react';
import { Github, Linkedin, Mail, ExternalLink } from 'lucide-react';
import { Logo } from './ui/Logo';
import { Container } from './ui/Container';

interface FooterLink {
  name: string;
  href: string;
  external?: boolean;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

const footerSections: FooterSection[] = [
  {
    title: 'Product',
    links: [
      { name: 'Features', href: '/features' },
      { name: 'Documentation', href: '/docs' },
      { name: 'Changelog', href: 'https://github.com/loopxdev/loopx-vscode-plugin/releases', external: true },
      { name: 'Roadmap', href: 'https://github.com/loopxdev/loopx-vscode-plugin/projects', external: true },
    ],
  },
  {
    title: 'Resources',
    links: [
      { name: 'Getting Started', href: '/docs' },
      { name: 'Examples', href: 'https://github.com/loopxdev/loopx-examples', external: true },
      { name: 'Best Practices', href: '/docs/best-practices' },
      { name: 'FAQ', href: '/docs/faq' },
    ],
  },
  {
    title: 'Community',
    links: [
      { name: 'GitHub', href: 'https://github.com/loopxdev/loopx', external: true },
      { name: 'Discussions', href: 'https://github.com/loopxdev/loopx-vscode-plugin/discussions', external: true },
      { name: 'Issues', href: 'https://github.com/loopxdev/loopx-vscode-plugin/issues', external: true },
      { name: 'Contributing', href: 'https://github.com/loopxdev/loopx-vscode-plugin/blob/main/CONTRIBUTING.md', external: true },
    ],
  },
  {
    title: 'Support',
    links: [
      { name: 'Contact Us', href: 'mailto:support@loopx.dev', external: true },
      { name: 'Bug Reports', href: 'https://github.com/loopxdev/loopx-vscode-plugin/issues/new?template=bug_report.md', external: true },
      { name: 'Feature Requests', href: 'https://github.com/loopxdev/loopx-vscode-plugin/issues/new?template=feature_request.md', external: true },
      { name: 'Security', href: 'mailto:security@loopx.dev', external: true },
    ],
  },
];

const socialLinks = [
  {
    name: 'GitHub',
    href: 'https://github.com/loopxdev/loopx-vscode-plugin',
    icon: Github,
    label: 'Follow us on GitHub',
  },
  {
    name: 'LinkedIn',
    href: 'https://linkedin.com/company/loopx-dev',
    icon: Linkedin,
    label: 'Follow us on LinkedIn',
  },
  {
    name: 'Email',
    href: 'mailto:hello@loopx.dev',
    icon: Mail,
    label: 'Send us an email',
  },
];

export const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const renderLink = (link: FooterLink) => {
    const disabledClasses = 'text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-60 px-1 py-0.5';

    // Disable all links - they will be available at launch
    return (
      <span
        key={link.name}
        className={`${disabledClasses} inline-flex items-center gap-1`}
        title="Available at launch - June 12th, 2026"
      >
        {link.name}
        {link.external && <ExternalLink className="h-3 w-3" aria-hidden="true" />}
      </span>
    );
  };

  return (
    <footer id="footer" className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800" role="contentinfo">
      <Container>
        <div className="py-12 lg:py-16">
          {/* Main footer content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-12">
            {/* Brand section */}
            <div className="lg:col-span-2">
              <div className="flex items-center mb-4">
                <Logo size={56} variant="svg" />
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-sm">
                Complete SDLC automation in VSCode. From requirements to deployment,
                powered by GitHub Copilot.
              </p>

              {/* Social links */}
              <div className="flex space-x-4" role="list" aria-label="Social media links">
                {socialLinks.map((social) => {
                  const Icon = social.icon;
                  const isLinkedIn = social.name === 'LinkedIn';

                  if (isLinkedIn) {
                    // Keep LinkedIn active
                    return (
                      <a
                        key={social.name}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-md p-1"
                        aria-label={social.label}
                        role="listitem"
                      >
                        <Icon className="h-5 w-5" />
                      </a>
                    );
                  } else {
                    // Disable other social links
                    return (
                      <span
                        key={social.name}
                        className="text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-60 p-1"
                        title="Available at launch - June 12th, 2026"
                        aria-label={`${social.name} - Available at launch`}
                        role="listitem"
                      >
                        <Icon className="h-5 w-5" />
                      </span>
                    );
                  }
                })}
              </div>
            </div>

            {/* Footer sections */}
            {footerSections.map((section) => (
              <nav key={section.title} className="lg:col-span-1" aria-labelledby={`footer-${section.title.toLowerCase()}-heading`}>
                <h3
                  id={`footer-${section.title.toLowerCase()}-heading`}
                  className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4"
                >
                  {section.title}
                </h3>
                <ul className="space-y-3">
                  {section.links.map((link) => (
                    <li key={link.name}>
                      {renderLink(link)}
                    </li>
                  ))}
                </ul>
              </nav>
            ))}
          </div>

          {/* Bottom section */}
          <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-800">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  © {currentYear} LoopX. All rights reserved.
                </p>
                <div className="flex space-x-6">
                  <span
                    className="text-sm text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-60"
                    title="Available at launch - June 12th, 2026"
                  >
                    Privacy Policy
                  </span>
                  <span
                    className="text-sm text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-60"
                    title="Available at launch - June 12th, 2026"
                  >
                    Terms of Service
                  </span>
                </div>
              </div>

              <div className="text-sm text-gray-500 dark:text-gray-400">
                Made with ❤️ for developers
              </div>
            </div>
          </div>
        </div>
      </Container>
    </footer>
  );
};

export default Footer;